import { i18n } from '@/plugins/i18n';
import { TransStatement, PayChannelSuccessRate, BetHistoryEntity } from './api/model';
import { BANK_PAY_CH_IDS, PaySource, TransactionType } from './config';
import { ExternalTransactionKind, ExternalTransactionType } from './bot/enum/ExternalTransactionType';

const DELAY_THRESHOLD = {
  BANK_DEPOSIT_DELAY_MINUTES: 60,
  CARD_DEPOSIT_DELAY_MINUTES: 10,
};

export const TRANSACTION_PREPOSITION_ENUM = {
  [TransactionType.Deposit]: 'from',
  [TransactionType.Withdrawal]: 'to',
  [TransactionType.Bet]: 'of',
} as const;

export const TRANSACTION_STATUS_ENUM = {
  'PENDING': 10,
  'SUCCESSFUL': 20,
  'FAILED': 30,
  'UNKNOWN': 90,
} as const;

export const TRANSACTION_STATUS_NAME_ENUM = {
  10: 'Pending',
  20: 'Successful',
  30: 'Failed',
  90: 'Unknown',
} as const;

export const UPDATED_STATUS_WARNING_VARIANTS = {
  'WARNING': 'warning',
  'SUCCESSFUL': 'successful',
  'PROMINENT': 'prominent',
} as const;

export type TransactionStatus = typeof TRANSACTION_STATUS_ENUM[keyof typeof TRANSACTION_STATUS_ENUM];
export type TransactionStatusName = typeof TRANSACTION_STATUS_NAME_ENUM[keyof typeof TRANSACTION_STATUS_NAME_ENUM];
export type UpdatedStatusWarningVariant = typeof UPDATED_STATUS_WARNING_VARIANTS[keyof typeof UPDATED_STATUS_WARNING_VARIANTS];

const SELECTION_EVENT_STATUS = {
  FINISHED: 4,
};

const TOURNAMENT_ID = {
  SERIES_D: 'sr:tournament:39221',
};

const WINNING_STATUS_ENUM = {
  RUNNING: 0,
  PARTIAL_WIN: 5,
  WON: 20,
  LOST: 30,
  VOID: 40,
  PENDING: 90,
};
interface TransactionWarning {
  show: boolean;
  message: string;
  kind?: 'prominent';
}

export function checkTransactionWarning ({ transaction, transactionMethodSuccessRate, type: transactionType }: {transactionMethodSuccessRate?: PayChannelSuccessRate[]|null, transaction: TransStatement, type: TransactionType}): TransactionWarning {
  const hasPaymentProviderIssue = getTransactionProviderHasIssue(transaction, transactionMethodSuccessRate);

  if (hasPaymentProviderIssue) {
    const providerMessage = transaction.counterAuthority && i18n.t('page.issue.transaction.payment_method_issue.provider', { provider: `<strong>${transaction.counterAuthority}</strong>` }) as string;
    const message = providerMessage || (transactionType === TransactionType.Deposit ? i18n.t('page.issue.transaction.payment_method_issue.deposit') : i18n.t('page.issue.transaction.payment_method_issue.withdrawal'));
    return {
      show: true,
      message: message as string,
      kind: 'prominent' as const,
    };
  }

  if (transactionType === TransactionType.Deposit) {
    return getTransactionWarning(transaction);
  }

  return { show: false, message: '' };
}

export function getTransactionWarning (transaction: TransStatement): TransactionWarning {
  const isCard = transaction.paySource === PaySource.CARD;
  const isBank = BANK_PAY_CH_IDS.includes(transaction.payChId);

  const possibleDelay = isCard ? DELAY_THRESHOLD.CARD_DEPOSIT_DELAY_MINUTES : isBank ? DELAY_THRESHOLD.BANK_DEPOSIT_DELAY_MINUTES : 0;
  const isRecentlyCreated = transaction.createTime > +new Date() - possibleDelay * 60 * 1000;

  if (!isRecentlyCreated) {
    return { show: false, message: '' };
  }

  const delayMessage = `<strong>${i18n.t('page.issue.transaction.minutes_delay', { delay: possibleDelay })}</strong>`;

  if (isCard) {
    return { show: true, message: i18n.t('page.issue.transaction.pending_card_transfer', { delay: delayMessage }) as string };
  }

  if (isBank) {
    return { show: true, message: i18n.t('page.issue.transaction.pending_bank_transfer', { delay: delayMessage }) as string };
  }

  return { show: false, message: '' };
}

function getTransactionProviderHasIssue (selectedTransaction: TransStatement, transactionMethodSuccessRate?: PayChannelSuccessRate[]|null) {
  if (!transactionMethodSuccessRate?.length) return false;
  const showAlert = [selectedTransaction].some(transaction => transactionMethodSuccessRate.some(sr => {
    if (sr.payChannelId !== transaction.payChId || !sr.networks?.length) return false;
    // when network === unknown, then we get only one record in the array.
    const isUnknown = sr.networks.length === 1 && (sr.networks[0]?.network || '').toUpperCase() === 'UNKNOWN';
    if (isUnknown) return sr.networks[0].displayAlert;
    return sr.networks.some(n => n.network === transaction.network && n.displayAlert);
  }));

  return showAlert;
}

export const isCardOrBankTransaction = (transaction: TransStatement) => transaction.paySource === PaySource.CARD || transaction.paySource === PaySource.BANK;

export function checkBetTransactionWarning ({ transaction }: { transaction: BetHistoryEntity }) {
  const seriesDTournament = transaction.selections.find(selection => selection.tournamentId === TOURNAMENT_ID.SERIES_D);

  const isSeriesDTournament = !!seriesDTournament;
  const isSeriesDTournamentFinished = seriesDTournament?.eventStatus === SELECTION_EVENT_STATUS.FINISHED;

  const isBetNotFinished =
    transaction.winningStatus === WINNING_STATUS_ENUM.RUNNING ||
    transaction.winningStatus === WINNING_STATUS_ENUM.PENDING;

  if (isSeriesDTournament && isSeriesDTournamentFinished && isBetNotFinished) {
    return {
      show: true,
      message: i18n.t('page.issue.transaction.tournament.series_d') as string,
      kind: 'prominent' as const,
    };
  }

  return { show: false, message: '' };
}
